import { useState } from "react";
import { ThumbsUp, ThumbsDown, Check, Loader2, UserRound } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

interface Props {
  surface: "member" | "internal";
  conversationId: string | null;
  messageId?: string;
  question: string;
  answer: string;
  sources: unknown[];
}

type State = "idle" | "prompting" | "submitting" | "offer" | "sending" | "done";

export function StewFeedbackControl({
  surface,
  conversationId,
  messageId,
  question,
  answer,
  sources,
}: Props) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [state, setState] = useState<State>("idle");
  const [comment, setComment] = useState("");
  const [savedComment, setSavedComment] = useState("");

  const insert = async (rating: "up" | "down", note?: string) => {
    if (!user || !question) return false;
    setState("submitting");
    const { error } = await supabase.from("stew_feedback").insert({
      surface,
      rating,
      question,
      answer,
      sources: (sources as any) ?? [],
      comment: note?.trim() || null,
      conversation_id: conversationId,
      message_ref: messageId ?? null,
      created_by: user.id,
    });
    if (error && !/duplicate key/i.test(error.message)) {
      toast({
        title: "Could not save feedback",
        description: error.message,
        variant: "destructive",
      });
      setState("idle");
      return false;
    }
    if (rating === "down" && surface === "member") {
      // Offer the member a one-tap escalation instead of jumping straight to "done".
      setSavedComment(note?.trim() || "");
      setState("offer");
      toast({ title: "Logged for the team." });
      return true;
    }
    setState("done");
    toast({
      title: rating === "up" ? "Noted, thank you." : "Logged for the team.",
    });
    return true;
  };

  const sendToPerson = async () => {
    if (!user || !question) return;
    setState("sending");
    const parts = [
      `My question to Stew:\n${question}`,
      `Stew's answer:\n${answer}`,
    ];
    if (savedComment) {
      parts.push(`What was off:\n${savedComment}`);
    }
    const questionText = parts.join("\n\n");

    const { data: q, error: qErr } = await supabase
      .from("congregation_questions")
      .insert({
        user_id: user.id,
        question_text: questionText,
        route: "private",
        status: "open",
      })
      .select("id")
      .single();
    if (qErr || !q) {
      toast({
        title: "Could not send this to a person",
        description: qErr?.message || "Please try again.",
        variant: "destructive",
      });
      setState("offer");
      return;
    }

    // Best-effort escalation record so /admin/support/escalations surfaces it.
    // Failure here is non-blocking; the member has already been queued in the questions inbox.
    const { error: eErr } = await supabase.from("escalation_log").insert({
      source: "thumbs_down",
      user_id: user.id,
      question,
      answer_excerpt: answer.slice(0, 500),
      reference_table: "congregation_questions",
      reference_id: q.id,
      status: "new",
    });
    if (eErr) {
      // Log but do not block: the member already has a real ticket.
      console.warn("escalation_log insert failed", eErr.message);
    }

    setState("done");
    toast({
      title: "Sent to a person.",
      description: "Someone from the team will get back to you.",
    });
  };

  if (state === "done") {
    return (
      <div className="flex items-center gap-1 text-[11px] text-muted-foreground pt-1">
        <Check className="h-3 w-3" /> Thanks
      </div>
    );
  }

  if (state === "offer" || state === "sending") {
    return (
      <div className="pt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-[12px] text-muted-foreground">
          Want a person to see this?
        </span>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void sendToPerson()}
          disabled={state === "sending"}
          className="h-7 px-2 text-[12px]"
        >
          {state === "sending" ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <UserRound className="h-3 w-3 mr-1" />
          )}
          Send to a person
        </Button>
        <button
          type="button"
          onClick={() => setState("done")}
          className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2"
        >
          No thanks
        </button>
      </div>
    );
  }

  if (state === "prompting") {
    return (
      <div className="pt-1 space-y-1.5">
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="What was off? Optional."
          rows={2}
          className="text-sm resize-none"
        />
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void insert("down", comment)}
          >
            Submit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void insert("down")}
          >
            Skip
          </Button>
        </div>
      </div>
    );
  }

  const busy = state === "submitting";

  return (
    <div className="flex items-center gap-1 pt-1 opacity-40 hover:opacity-100 focus-within:opacity-100 transition-opacity">
      <button
        type="button"
        onClick={() => void insert("up")}
        disabled={busy}
        aria-label="Helpful"
        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ThumbsUp className="h-3 w-3" />}
      </button>
      <button
        type="button"
        onClick={() => setState("prompting")}
        disabled={busy}
        aria-label="Not helpful"
        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-50"
      >
        <ThumbsDown className="h-3 w-3" />
      </button>
    </div>
  );
}
